import { Dispatch, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { groupBy } from 'ramda';
import equal from 'fast-deep-equal';
import { v4 as uuidv4 } from 'uuid';

import { Button } from 'UIPackage/MainModule/components/Button';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  getCurrentNpaAction,
  getNpaListNewAction,
  setIsNpaCreatedAction,
  setIsNpaEditedAction,
  setModalModeAction,
  setNpaListNew,
  setSelectedNpaAction,
} from 'KnmPackage/RequirementsNpaModalModule/actions/requirementsNpaActions';
import {
  getActiveDuplicateNpa,
  getIdMap,
  getIsNpaCreated,
  getIsNpaEdited,
  getNpaListNew,
  getSavedNpa,
} from 'KnmPackage/RequirementsNpaModalModule/selectors/requirementsNpaSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { IRequirementNpaResponse } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpaResponse';
import { RequirementsNpaSourceEnum } from 'KnmPackage/RequirementsNpaModalModule/enums/RequirementsNpaSourceEnum';
import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import {
  fetchStructuralUnitsByOldFormat,
  fetchStructuresUnitsAsTree,
  structuralUnitConverter,
} from 'APIPackage/RequirementsNpaModule/services/requirementsNpaApi';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IGetStructureUnitFlat } from 'APIPackage/RequirementsNpaModule/interfaces/IGetStructureUnitFlat';
import { RequirementsModalModeEnum } from 'KnmPackage/RequirementsNpaModalModule/enums/RequirementsModalModeEnum';
import { IRequirementsNpaItem } from 'KnmPackage/RequirementsNpaModalModule/interfaces/IRequirementsNpaItem';
import { IRequirementsNpaItemWithoutMandatory } from 'KnmPackage/RequirementsNpaModalModule/interfaces/IRequirementsNpaItemWithoutMandatory';
import { IRequirementsIdMap } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementsIdMap';
import {
  IRequirementsMandatoryNew,
  IStructuresUnitsAsTreeResponse,
} from 'APIPackage/RequirementsNpaModule/interfaces/IStructuresUnitsAsTreeResponse';

import { Modal } from '../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../UIPackage/ModalModule/components/ModalHeader';
import styles from './StructureRequirementsNpaModal.module.scss';
import { NpaItem } from './NpaItem';
import { InputFormRequirementsNpa, ISelectedStructuralUnit } from './interface';
import { CreateEditNpaModal } from './SubModals/CreateEditNpaModal/CreateEditNpaModal';
import { CreateSuAndMandatoryModal } from './SubModals/CreateSuAndMandatoryModal/CreateSuAndMandatoryModal';
import { convertSuToHierarchyString } from './SubModals/convertSuToHierarchyString';
import { updateNodeDataById } from './function';
import { IKnmErknmFormObject } from '../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../MainModule/interfaces/IKnmFormRiskIndicators';
import { IKnmErknmFormRequirementNpa } from '../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { IKnmPmFormRequirementsNpa } from '../../KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { isLaterThanAdminSetting } from '../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getKnmDataCreated } from '../MainModule/selectors/knmSelectors';
import { getDecisionDataCreatedAt } from '../../DecisionPackage/MainModule/selectors/decisionSelectors';
import { getAdminFetchSettings } from '../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getCoreIsLoading } from '../../CorePackage/MainModule/selectors/coreSelectors';
import { convertFormDateValueToKnmDataDate } from '../MainModule/common/convertFormDateValueToKnmDataDate';
import { coreSetLoading } from '../../CorePackage/MainModule/actions/coreActions';
import { addNoticeAction } from '../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../CorePackage/NotificationModule/enums/MessageTypesEnum';

interface IStructureRequirementsNpaModalProps {
  addAllFields?: () => void;
  addRequirementsNpa?: (npa: IRequirementsNpaItem[]) => void;
  addRequirementsNpaWithoutMandatory?: (npa: IRequirementsNpaItemWithoutMandatory[]) => void;
  ignoredNpaIds?: number[];
  initValues?: IRequirementsNpaInitValues;
  inputRequirementsNpa?: InputFormRequirementsNpa[];
  knoVersionId: string;
  mrIds?: number[];
  npasAlreadyFilled?: boolean;
  objects?: IKnmErknmFormObject[];
  onClose: () => void;
  onNpaEdited?: (npa: IRequirementNpa, idMap: IRequirementsIdMap | null) => void;
  riskIndicators?: IKnmFormRiskIndicators[];
  supervisionVersionId: string;
  usedNpa?: IKnmErknmFormRequirementNpa[] | IKnmPmFormRequirementsNpa[];
  withRequirements: boolean;
}

interface IStructureRequirementsNpaModalStoreProps {
  activeDuplicateNpa: IRequirementNpaResponse | null;
  idMap: IRequirementsIdMap | null;
  isNpaCreated: boolean;
  isNpaEdited: boolean;
  npaListNew: IRequirementNpaResponse | null;
  savedNpa: IRequirementNpa | null;
  created: string | null | undefined;
  decisionDataCreated?: string | null;
  settings: IAdminSetting[];
  isLoading: boolean;
}

type ILocalStructures = Record<number, IStructuresUnitsAsTreeResponse | undefined>;

const StructureRequirementsNpaModalWithoutStore = ({
  addAllFields,
  addRequirementsNpa,
  addRequirementsNpaWithoutMandatory,
  ignoredNpaIds,
  initValues,
  inputRequirementsNpa,
  knoVersionId,
  mrIds: mrIdsProp,
  npasAlreadyFilled,
  objects,
  onClose,
  onNpaEdited,
  riskIndicators,
  supervisionVersionId,
  usedNpa,
  withRequirements,
  activeDuplicateNpa,
  idMap,
  isNpaCreated,
  isNpaEdited,
  npaListNew,
  savedNpa,
  created,
  decisionDataCreated,
  settings,
  isLoading,
}: IStructureRequirementsNpaModalProps & IStructureRequirementsNpaModalStoreProps) => {
  const isLaterThanNpaValidationSetting = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_STARTS_FROM,
  );

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isOpenModalType, setIsOpenModalType] = useState<RequirementsModalModeEnum>();
  const [searchString, setSearchString] = useState('');
  const [didCloseInnerModal, setDidClosInnerModal] = useState(false);
  const [editingItem, setEditingItem] = useState<IRequirementNpa>();
  const [addMandatoryPath, setAddMandatoryPath] = useState('');
  const [addMandatorySuId, setAddMandatorySuId] = useState<number>(-1);
  let defaultNpasOpen = {};
  let defaultSusOpen = {};

  if (usedNpa) {
    usedNpa.forEach(preSelectedNpa => {
      defaultNpasOpen = { ...defaultNpasOpen, [preSelectedNpa.npa.id]: true };
      if (preSelectedNpa.structuralUnit.title) {
        const resultString = convertSuToHierarchyString(preSelectedNpa.structuralUnit.title);

        defaultSusOpen = { ...defaultSusOpen, [`${preSelectedNpa.npa.id}/${resultString}`]: true };
      }
    });
  } else if (initValues) {
    defaultNpasOpen = { [initValues.npa.id]: true };
    if (initValues.structuralUnit.title) {
      const resultString = convertSuToHierarchyString(initValues.structuralUnit.title);

      defaultSusOpen = { [`${initValues.npa.id}/${resultString}`]: true };
    }
  }

  const [isNpasOpen, setIsNpasOpen] = useState<{ [key: number]: boolean }>(defaultNpasOpen);

  const [isSusOpen, setIsSusOpen] = useState<{ [key: string]: boolean }>(defaultSusOpen);
  const [npaList, setNpaList] = useState<IRequirementNpaResponse | null>(npaListNew);
  const [localStructures, setLocalStructures] = useState<ILocalStructures>({});
  const [suIdsSelected, setSuIdsSelected] = useState<ISelectedStructuralUnit[]>([]);
  const [isNextProcessing, setIsNextProcessing] = useState(false);

  useEffect(() => {
    // Если в предмете выбраны ОТ, то отображаем только их при выборе нарушений в акте
    const npaIds = inputRequirementsNpa?.map(e => e.npa.id);

    dispatch(getNpaListNewAction({ knoVersionId, supervisionVersionId, npaIds: [...new Set(npaIds)] }));
    initValues?.npa?.id && dispatch(getCurrentNpaAction(initValues.npa.id, knoVersionId, supervisionVersionId));

    return () => {
      dispatch(setNpaListNew(null));
    };
  }, []);

  useEffect(() => {
    if (initValues) {
      dispatch(setSelectedNpaAction(initValues.npa));
      dispatch(setModalModeAction(RequirementsModalModeEnum.SELECT_MANDATORY));
    } else {
      dispatch(setSelectedNpaAction(null));
    }
  }, [initValues, dispatch]);

  useEffect(() => {
    // Если в предмете выбраны ОТ, то отображаем только их при выборе нарушений в акте
    const npaIds = inputRequirementsNpa?.map(e => e.npa.id);

    if (isNpaCreated) {
      dispatch(getNpaListNewAction({ knoVersionId, supervisionVersionId, npaIds: [...new Set(npaIds)] }));
      dispatch(setIsNpaCreatedAction(false));
      initValues?.npa?.id && dispatch(getCurrentNpaAction(initValues.npa.id, knoVersionId, supervisionVersionId));
      savedNpa && setTimeout(() => scrollToNpa(savedNpa), 400);
    }
    if (isNpaEdited) {
      if (suIdsSelected.find(i => i.npaId === idMap?.oldNpaId)) {
        setSuIdsSelected(values =>
          values.map(suItem => {
            const su = { ...suItem };

            if (su.npaId === idMap?.oldNpaId) {
              su.npaId = idMap?.npaId;
            }

            return su;
          }),
        );
      }

      const newObj = { ...isNpasOpen };

      newObj[idMap?.npaId || -1] = true;
      setIsNpasOpen(newObj);
      dispatch(getNpaListNewAction({ knoVersionId, supervisionVersionId, npaIds: [...new Set(npaIds)] }));
      initValues?.npa?.id && dispatch(getCurrentNpaAction(initValues.npa.id, knoVersionId, supervisionVersionId));
      dispatch(setIsNpaEditedAction(false));
      onNpaEdited && savedNpa && onNpaEdited(savedNpa, idMap);
    }
  }, [dispatch, isNpaCreated, isNpaEdited, knoVersionId, supervisionVersionId, inputRequirementsNpa]);

  useEffect(() => {
    if (npaListNew && !npaList) {
      setNpaList(npaListNew);
    }

    const npaListCleared = {
      ...npaList,
      requirements:
        npaList?.requirements?.map(requirement => {
          const newRequirement = { ...requirement };

          delete newRequirement.structures;

          return newRequirement;
        }) || [],
    };

    if (isNpaCreated || isNpaEdited || !equal(npaListNew, npaListCleared)) {
      setNpaList(npaListNew);
    }
  }, [npaListNew, isNpaCreated, isNpaEdited, npaList]);

  const npaListView = useMemo(() => {
    if (!npaList) return [];

    if (npasAlreadyFilled) {
      const requirements = activeDuplicateNpa?.requirements ?? [];

      return requirements.map(item => ({
        ...item,
        structures: localStructures[item.id],
      }));
    }

    return npaList.requirements
      .filter(
        i =>
          ((i.title || '').toLowerCase().includes(searchString.toLowerCase()) ||
            (i.number || '').toLowerCase().includes(searchString.toLowerCase())) &&
          (!initValues ? true : initValues.npa.id === i.id),
      )
      .map(item => ({
        ...item,
        structures: localStructures[item.id],
      }));
  }, [npaList, activeDuplicateNpa, npasAlreadyFilled, searchString, initValues, localStructures]);

  const isCustom = useMemo(() => {
    // Если НПА были выбраны ранее в паспорте
    if (npasAlreadyFilled) {
      // Если npaListView не содержит элементов, то возвращаем true когда npaCreateDisabled = false
      if (npaListView.length === 0) {
        return npaList?.npaCreateDisabled === false;
      }

      return npaListView[0]?.source === RequirementsNpaSourceEnum.CUSTOM;
    }

    if (npaList?.requirements.length) {
      return npaListView[0]?.source === RequirementsNpaSourceEnum.CUSTOM;
    }

    return false;
  }, [npaList]);

  const npaCountTitle = useMemo(() => {
    return `Найдено записей ${isCustom ? 'в ЕРКНМ' : 'во ФГИС РОТ'} - ${npaListView.length}`;
  }, [npaListView, isCustom]);

  const isDisabledBtnAddSelected = useMemo(() => {
    return suIdsSelected.length === 0;
  }, [suIdsSelected]);

  const onUpdateStructuresHandler = useCallback((item: IRequirementNpa) => {
    if (item.structures) {
      setLocalStructures(prev => ({
        ...prev,
        [item.id]: item.structures,
      }));
    }
  }, []);

  const onSelectedHandler = useCallback(async () => {
    dispatch(coreSetLoading(true));
    setIsNextProcessing(true);

    try {
      const byNpaId = groupBy<ISelectedStructuralUnit>(item => item.npaId.toString());
      const bySuId = groupBy<ISelectedStructuralUnit>(item => item.id.toString());

      const groupedSu = byNpaId(suIdsSelected);

      const params: IGetStructureUnitFlat = {
        units: Object.keys(groupedSu).map(key => {
          const groupedSuById = bySuId(groupedSu[key]);

          return {
            npaId: key,
            structuralUnits: Object.keys(groupedSuById).map(keyById => {
              return {
                id: keyById,
                mandatoryRequirementIds: groupedSuById[keyById].map(i => i.mandatoryId?.toString() || ''),
              };
            }),
          };
        }),
      };

      const data = await fetchStructuralUnitsByOldFormat(params);

      if (addRequirementsNpa) {
        const result: IRequirementsNpaItem[] = [];

        data.forEach(item => {
          item.structuralUnits.forEach(su => {
            const resultObj: IRequirementsNpaItem = {
              guid: uuidv4(),
              npa: item.npa,
              structuralUnit: structuralUnitConverter(su.structuralUnit),
              mandatory: su.mandatoryRequirements,
            };

            result.push(resultObj);
          });
        });

        addRequirementsNpa(result);
      }

      if (addRequirementsNpaWithoutMandatory) {
        const result: IRequirementsNpaItemWithoutMandatory[] = [];

        data.forEach(item => {
          const resultObj: IRequirementsNpaItemWithoutMandatory = {
            npa: item.npa,
            structuralUnits: item.structuralUnits.map(i => structuralUnitConverter(i.structuralUnit)),
          };

          result.push(resultObj);
        });

        addRequirementsNpaWithoutMandatory(result);
      }

      onClose();
    } catch (e) {
      dispatch(
        addNoticeAction({
          renderContent: 'Не удалось добавить обязательные требования',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    } finally {
      dispatch(coreSetLoading(false));
      setIsNextProcessing(false);
    }
  }, [suIdsSelected, addRequirementsNpa, addRequirementsNpaWithoutMandatory, onClose, dispatch]);

  const onOpenNpaForEdit = useCallback((e: MouseEvent<HTMLButtonElement>, item: IRequirementNpa) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpenModalType(RequirementsModalModeEnum.EDIT_NPA);
    setEditingItem(item);
  }, []);

  const onOpenCreateSu = useCallback((item: IRequirementNpa) => {
    setIsOpenModalType(RequirementsModalModeEnum.CREATE_STRUCTURAL_UNIT);
    setEditingItem(item);
  }, []);

  const onOpenAddMandatory = useCallback((item: IRequirementNpa, path: string, suId: number) => {
    setIsOpenModalType(RequirementsModalModeEnum.CREATE_STRUCTURAL_UNIT);
    setEditingItem(item);
    setAddMandatoryPath(path);
    setAddMandatorySuId(suId);
  }, []);

  const structuresUnitsFilters = useMemo(() => {
    if (!isLaterThanNpaValidationSetting) {
      return {};
    }

    const objectTypeIds = objects
      ?.map(e => e.objectType?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const objectKindIds = objects
      ?.map(e => e.objectKind?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const objectSubkindIds = objects
      ?.map(e => e.objectSubKind?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const riskIndicatorIds = riskIndicators
      ?.map(e => e.riskIndicator?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    return {
      objectTypeIds,
      objectKindIds,
      objectSubkindIds,
      riskIndicatorIds,
    };
  }, [objects, riskIndicators, isLaterThanNpaValidationSetting]);

  const scrollToNpa = useCallback(
    async (npa?: IRequirementNpa, newSuId?: string, newMandatoryId?: string) => {
      if (!npa) return;

      let suIds: number[] = [];
      const mrIds: number[] = [];

      if (inputRequirementsNpa) {
        suIds = inputRequirementsNpa.map(e => e.structuralUnit.id);
        inputRequirementsNpa.forEach(e => {
          e?.mandatory?.forEach(m => {
            m.id && mrIds.push(m.id);
          });
        });
      }

      const units = await fetchStructuresUnitsAsTree({
        npaId: npa.id,
        knmCreationDate: convertFormDateValueToKnmDataDate(created) || undefined,
        knoVersionId,
        supervisionVersionId,
        withRequirements,
        suId: initValues?.structuralUnit?.id?.toString(),
        suIds,
        mrIds,
        ...structuresUnitsFilters,
      });

      onUpdateStructuresHandler({ ...npa, structures: units });

      let myElement: HTMLElement | null;

      if (newMandatoryId) {
        myElement = document.getElementById(newMandatoryId.toString());
        if (!myElement && newSuId) {
          myElement = document.getElementById(newSuId.toString());
        }
      } else if (newSuId) {
        myElement = document.getElementById(newSuId.toString());
      } else {
        myElement = document.getElementById(npa.id.toString());
      }

      if (myElement?.offsetTop) {
        const topPos = myElement.offsetTop - 320;
        const scrollTable = document.getElementById('scroll-table');

        if (scrollTable) {
          setTimeout(() => {
            scrollTable.scrollTop = topPos;
          }, 500);
        }
      }
    },
    [knoVersionId, supervisionVersionId, withRequirements, initValues],
  );

  const onCloseInnerModal = useCallback(
    (newSuId?: string, newMandatoryId?: string, npaWasEdit?: boolean) => {
      setDidClosInnerModal(true);
      setTimeout(() => setDidClosInnerModal(false), 200);
      if (!npaWasEdit) {
        setTimeout(() => scrollToNpa(editingItem, newSuId, newMandatoryId), 500);
      }
      if (npaWasEdit && editingItem) {
        const newObj = { ...isNpasOpen };

        newObj[editingItem.id] = false;
        setIsNpasOpen(newObj);
      }
      setIsOpenModalType(undefined);
      setEditingItem(undefined);
      setAddMandatoryPath('');
      setAddMandatorySuId(-1);
    },
    [scrollToNpa, editingItem, isNpasOpen],
  );

  const seIsOpenNpa = useCallback(
    (npaId: number, val: boolean) => {
      const newObj = { ...isNpasOpen };

      newObj[npaId] = val;
      setIsNpasOpen(newObj);
    },
    [isNpasOpen],
  );

  const setIsOpenSus = useCallback(
    (path: string, val: boolean) => {
      const newObj = { ...isSusOpen };

      newObj[path] = val;
      setIsSusOpen(newObj);
    },
    [isSusOpen],
  );

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
      setIsSusOpen({});
      setIsNpasOpen({});
    },
    [setSearchString, setIsNpasOpen, setIsSusOpen],
  );

  const onCloseNpaHandler = useCallback((npaId: number) => {
    const newObj: {
      [key: string]: boolean;
    } = {};

    Object.keys(isSusOpen).forEach(key => {
      if (key.indexOf(npaId.toString()) === -1) {
        newObj[key] = isSusOpen[key];
      }
    });

    setIsSusOpen(newObj);
  }, []);

  const updateNpaList = useCallback((npaId: number, suId: string, list: IRequirementsMandatoryNew[]) => {
    setLocalStructures(prev => {
      if (!prev[npaId]) return prev;
      const updatedStructures = updateNodeDataById(prev[npaId], suId, list);

      return {
        ...prev,
        [npaId]: updatedStructures,
      };
    });
  }, []);

  return (
    <>
      <Modal
        visible={
          !(
            isOpenModalType === RequirementsModalModeEnum.CREATE_NPA ||
            isOpenModalType === RequirementsModalModeEnum.EDIT_NPA
          ) && !(editingItem && isOpenModalType === RequirementsModalModeEnum.CREATE_STRUCTURAL_UNIT)
        }
        onClose={onClose}
        modalBodyClassName={styles.Modal}
      >
        <ModalHeader title={'Добавление обязательного требования'} />
        <div className={styles.Search}>
          <TextInput
            id="searchString"
            name="searchString"
            placeholder="Введите номер или часть наименования НПА"
            clearable={!!searchString}
            onChange={e => onSearch(e.target.value)}
            onClear={() => onSearch('')}
            value={searchString}
          />
        </div>
        {!inputRequirementsNpa && (
          <div className={styles.CountAndBtnCreate}>
            <span>{npaCountTitle}</span>
            {!initValues?.structuralUnit?.id && !npaList?.npaCreateDisabled && !npaList?.requirementsErknmDisabled && (
              <Button
                text="Создать новый НПА"
                variant="link"
                className={styles.CreateNpaButton}
                startIcon={styles.AddIcon}
                onClick={() => setIsOpenModalType(RequirementsModalModeEnum.CREATE_NPA)}
                type="button"
              />
            )}
          </div>
        )}
        {!inputRequirementsNpa && (
          <div className={styles.EmptySearch}>
            <span className={styles.InfoIcon}></span>

            <span className={styles.InformationSupportText}>
              {npaList?.npaCreateDisabled
                ? 'В случае отсутствия необходимых НПА, проверьте, что во ФГИС РОТ необходимые сведения по выбранным КНО и Виду контроля (надзора) внесены и подписаны'
                : 'Во ФГИС РОТ отсутствуют НПА для связки Вид контроля (надзора) и КНО, вид контроля не включен в план–график (постановление Правительства от 6.02.2021 № 128). НПА и структурные единицы вносятся в ЕРКНМ'}
            </span>
          </div>
        )}
        <div className={styles.Table}>
          <div className={styles.TableHeader}>
            <div />
            <div>№ И ДАТА</div>
            <div>НАИМЕНОВАНИЕ НПА</div>
          </div>
          <div className={styles.TableBody} id={'scroll-table'}>
            {npaListView.length > 0 &&
              npaListView.map(item => (
                <NpaItem
                  key={item.id}
                  isOpen={isNpasOpen[item.id]}
                  setIsOpen={val => seIsOpenNpa(item.id, val)}
                  setIsSuOpen={(path, val) => setIsOpenSus(path, val)}
                  item={item}
                  canEditNpa={isCustom}
                  knoVersionId={knoVersionId}
                  supervisionVersionId={supervisionVersionId}
                  structuresUnitsFilters={structuresUnitsFilters}
                  withRequirements={withRequirements}
                  onUpdateStructures={onUpdateStructuresHandler}
                  suIdsSelected={suIdsSelected}
                  setSuIdsSelected={setSuIdsSelected}
                  onOpenNpaForEdit={e => onOpenNpaForEdit(e, item)}
                  onClickAddNewSU={() => onOpenCreateSu(item)}
                  addMandatory={(path, suId) => onOpenAddMandatory(item, path, suId)}
                  isSusOpen={isSusOpen}
                  closeNpa={onCloseNpaHandler}
                  didCloseInnerModal={didCloseInnerModal}
                  uniqStructureId={initValues?.structuralUnit?.id?.toString()}
                  updateNpaList={updateNpaList}
                  inputRequirementsNpa={inputRequirementsNpa}
                  requirementsErknmDisabled={npaList?.requirementsErknmDisabled}
                  ignoredNpaIds={ignoredNpaIds}
                  mrIds={mrIdsProp}
                  usedNpa={usedNpa}
                />
              ))}
            {npaListView.length === 0 && searchString && (
              <div className={styles.EmptySearch}>
                <span className={styles.InfoIcon}></span>
                <span className={styles.InformationSupportText}>
                  По Вашему поисковому запросу не найдено ни одного НПА
                </span>
              </div>
            )}
            {npaListView.length === 0 && !searchString && !isLoading && (
              <div className={styles.EmptyNpaList}>
                <span>Необходимые значения не найдены.</span>
                <span>
                  Проверьте, что во ФГИС РОТ необходимые сведения по выбранным КНО и Виду контроля (надзора) внесены и
                  подписаны
                </span>
              </div>
            )}
          </div>
        </div>
        <ModalActions>
          {addAllFields && (
            <Button
              text="Добавить все ОТ по индикатору"
              size="medium"
              color="primary"
              type="submit"
              onClick={addAllFields}
              disabled={!riskIndicators?.length}
            />
          )}
          <Button
            text="Далее"
            size="medium"
            color="primary"
            type="submit"
            disabled={isDisabledBtnAddSelected || isNextProcessing}
            onClick={onSelectedHandler}
          />
          <Button text="Отмена" size="medium" onClick={onClose} disabled={isNextProcessing} />
        </ModalActions>
      </Modal>
      {(isOpenModalType === RequirementsModalModeEnum.CREATE_NPA ||
        isOpenModalType === RequirementsModalModeEnum.EDIT_NPA) && (
        <CreateEditNpaModal
          onClose={onCloseInnerModal}
          knoVersionId={knoVersionId}
          supervisionVersionId={supervisionVersionId}
          item={editingItem}
        />
      )}
      {editingItem && isOpenModalType === RequirementsModalModeEnum.CREATE_STRUCTURAL_UNIT && (
        <CreateSuAndMandatoryModal
          onClose={onCloseInnerModal}
          npaItem={editingItem}
          withRequirements={withRequirements}
          knoVersionId={knoVersionId}
          supervisionVersionId={supervisionVersionId}
          addMandatoryPath={addMandatoryPath}
          suId={addMandatorySuId}
          structuresUnitsFilters={structuresUnitsFilters}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IStructureRequirementsNpaModalStoreProps => ({
  npaListNew: getNpaListNew(state),
  isNpaCreated: getIsNpaCreated(state),
  isNpaEdited: getIsNpaEdited(state),
  savedNpa: getSavedNpa(state),
  idMap: getIdMap(state),
  activeDuplicateNpa: getActiveDuplicateNpa(state),
  created: getKnmDataCreated(state),
  decisionDataCreated: getDecisionDataCreatedAt(state),
  settings: getAdminFetchSettings(state),
  isLoading: getCoreIsLoading(state),
});

export const StructureRequirementsNpaModal = connect(mapStateToProps)(StructureRequirementsNpaModalWithoutStore);
